import { useState, useEffect } from "react";
import {
  Sparkles,
  Building2,
  Trophy,
  UserPlus,
  Users,
  Rocket,
  Check,
  Upload as UploadIcon,
  ChevronRight,
} from "lucide-react";

/**
 * Animated mockup that cycles through the 5 onboarding wizard steps inside a
 * laptop/window chrome. Pure CSS + React state - no video file required.
 */
const STEPS = [
  {
    key: "club",
    icon: Building2,
    label: "Dati Società",
    body: <ClubStep />,
  },
  {
    key: "team",
    icon: Trophy,
    label: "Prima Squadra",
    body: <TeamStep />,
  },
  {
    key: "staff",
    icon: UserPlus,
    label: "Invita Staff",
    body: <StaffStep />,
  },
  {
    key: "players",
    icon: Users,
    label: "Importa Giocatori",
    body: <PlayersStep />,
  },
  {
    key: "tour",
    icon: Rocket,
    label: "Sei pronto!",
    body: <TourStep />,
  },
];

const STEP_DURATION_MS = 3200;

export default function AnimatedWizardDemo() {
  const [stepIdx, setStepIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return undefined;
    const id = setInterval(() => {
      setStepIdx((i) => (i + 1) % STEPS.length);
    }, STEP_DURATION_MS);
    return () => clearInterval(id);
  }, [paused]);

  const current = STEPS[stepIdx];

  return (
    <div
      className="relative max-w-3xl mx-auto"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="landing-wizard-demo"
    >
      {/* Glow */}
      <div className="absolute -inset-8 bg-gradient-to-br from-emerald-500/30 via-sky-500/20 to-transparent blur-3xl rounded-full -z-10 pointer-events-none" />

      {/* Laptop window frame */}
      <div className="rounded-2xl overflow-hidden border border-slate-700/30 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.6)] bg-slate-900/80 backdrop-blur">
        {/* Browser chrome */}
        <div className="h-9 bg-slate-900/80 border-b border-slate-700/40 flex items-center px-4 gap-2">
          <span className="h-2.5 w-2.5 rounded-full bg-red-400/80" />
          <span className="h-2.5 w-2.5 rounded-full bg-yellow-400/80" />
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/80" />
          <div className="flex-1 flex justify-center">
            <div className="px-3 py-0.5 rounded-md bg-slate-800/80 text-[10px] text-slate-400 font-mono">
              calcioclub.app/setup
            </div>
          </div>
        </div>

        {/* Wizard content */}
        <div className="bg-white">
          {/* Wizard header */}
          <div className="bg-gradient-to-br from-emerald-700 via-emerald-600 to-sky-700 text-white px-6 py-4">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles size={14} className="text-emerald-100" />
              <span className="text-[10px] uppercase tracking-widest font-bold text-emerald-100">
                Setup guidato
              </span>
            </div>
            <div className="font-heading text-lg font-bold leading-tight">
              Benvenuto nel tuo spogliatoio digitale
            </div>
            {/* Stepper */}
            <div className="mt-3 flex items-center gap-1.5">
              {STEPS.map((s, i) => (
                <div
                  key={s.key}
                  className={`flex-1 h-1 rounded-full transition-all duration-500 ${
                    i < stepIdx
                      ? "bg-emerald-300"
                      : i === stepIdx
                      ? "bg-white"
                      : "bg-white/20"
                  }`}
                />
              ))}
            </div>
            <div className="mt-1.5 text-[10px] text-white/70">
              Passo {stepIdx + 1} di {STEPS.length} ·{" "}
              <span className="text-white font-medium">{current.label}</span>
            </div>
          </div>

          {/* Step body — keyed to force fade transition */}
          <div className="bg-slate-50 px-6 py-5 min-h-[260px] relative">
            <div
              key={stepIdx}
              className="animate-[fadeSlide_500ms_ease-out]"
              style={{ animationFillMode: "both" }}
            >
              {current.body}
            </div>
          </div>

          {/* Footer */}
          <div className="bg-white border-t border-slate-200 px-6 py-3 flex items-center justify-between">
            <button className="text-xs text-slate-400" disabled>
              ← Indietro
            </button>
            <button className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-emerald-600 text-white text-xs font-medium">
              {stepIdx === STEPS.length - 1 ? (
                <>
                  <Check size={12} /> Inizia
                </>
              ) : (
                <>
                  Avanti <ChevronRight size={12} />
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Step dots below */}
      <div className="mt-5 flex items-center justify-center gap-2">
        {STEPS.map((s, i) => (
          <button
            key={s.key}
            onClick={() => setStepIdx(i)}
            className={`h-2 rounded-full transition-all ${
              i === stepIdx ? "w-8 bg-emerald-500" : "w-2 bg-slate-300 hover:bg-slate-400"
            }`}
            aria-label={`Step ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

/* ---------------- Step content components ---------------- */

function ClubStep() {
  return (
    <div className="space-y-3">
      <div className="text-xs text-slate-500">
        Inserisci i dati della tua società. Tutti i campi sono opzionali.
      </div>
      <div>
        <div className="text-[10px] font-medium text-slate-600 mb-1">Logo società</div>
        <div className="flex items-center gap-2">
          <div className="h-10 w-10 rounded-lg bg-emerald-50 border border-dashed border-emerald-300 flex items-center justify-center">
            <Building2 size={14} className="text-emerald-600" />
          </div>
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-slate-200 bg-white text-[11px] text-slate-700">
            <UploadIcon size={11} /> Carica logo
          </div>
        </div>
      </div>
      <div>
        <div className="text-[10px] font-medium text-slate-600 mb-1">Denominazione società</div>
        <div className="h-8 rounded-md border border-slate-200 bg-white px-3 flex items-center text-[11px] text-slate-700">
          ASD Calcio Aurora
          <span className="ml-1 inline-block w-0.5 h-3.5 bg-emerald-500 animate-pulse" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <div className="text-[10px] font-medium text-slate-600 mb-1">Matricola FIGC</div>
          <div className="h-8 rounded-md border border-slate-200 bg-white px-3 flex items-center text-[11px] text-slate-700">
            123456
          </div>
        </div>
        <div>
          <div className="text-[10px] font-medium text-slate-600 mb-1">Presidente</div>
          <div className="h-8 rounded-md border border-slate-200 bg-white px-3 flex items-center text-[11px] text-slate-700">
            Mario Bianchi
          </div>
        </div>
      </div>
    </div>
  );
}

function TeamStep() {
  return (
    <div className="space-y-3">
      <div className="text-xs text-slate-500">
        Crea la tua prima squadra. Potrai aggiungerne altre dopo.
      </div>
      <div>
        <div className="text-[10px] font-medium text-slate-600 mb-1">Nome squadra *</div>
        <div className="h-8 rounded-md border border-emerald-400 ring-2 ring-emerald-100 bg-white px-3 flex items-center text-[11px] text-slate-700">
          Pulcini 2018
          <span className="ml-1 inline-block w-0.5 h-3.5 bg-emerald-500 animate-pulse" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <div className="text-[10px] font-medium text-slate-600 mb-1">Categoria FIGC</div>
          <div className="h-8 rounded-md border border-slate-200 bg-white px-3 flex items-center justify-between text-[11px] text-slate-700">
            Pulcini <ChevronRight size={10} className="rotate-90 text-slate-400" />
          </div>
        </div>
        <div>
          <div className="text-[10px] font-medium text-slate-600 mb-1">Stagione</div>
          <div className="h-8 rounded-md border border-slate-200 bg-white px-3 flex items-center justify-between text-[11px] text-slate-700">
            2025/2026 <ChevronRight size={10} className="rotate-90 text-slate-400" />
          </div>
        </div>
      </div>
      <div>
        <div className="text-[10px] font-medium text-slate-600 mb-1">Colore sociale</div>
        <div className="flex items-center gap-2">
          <div className="h-8 w-12 rounded-md bg-emerald-600 border border-slate-200" />
          <div className="h-8 flex-1 rounded-md bg-emerald-600 text-white text-[11px] font-medium px-3 flex items-center">
            #16a34a
          </div>
        </div>
      </div>
    </div>
  );
}

function StaffStep() {
  const rows = [
    { name: "andrea.allenatore@asd.it", role: "Dirigente" },
    { name: "preparatore@asd.it", role: "Dirigente" },
  ];
  return (
    <div className="space-y-3">
      <div className="text-xs text-slate-500">
        Invita dirigenti via email. Puoi farlo anche dopo.
      </div>
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={r.name} className="flex gap-2 items-center">
            <div className="flex-1 h-8 rounded-md border border-slate-200 bg-white px-3 flex items-center text-[11px] text-slate-700">
              {r.name}
              {i === rows.length - 1 && (
                <span className="ml-1 inline-block w-0.5 h-3.5 bg-emerald-500 animate-pulse" />
              )}
            </div>
            <div className="w-24 h-8 rounded-md border border-slate-200 bg-white px-2.5 flex items-center text-[11px] text-slate-700">
              {r.role}
            </div>
          </div>
        ))}
        <button className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-slate-200 bg-white text-[11px] text-slate-600 hover:bg-slate-50">
          <UserPlus size={11} /> Aggiungi altro invito
        </button>
      </div>
    </div>
  );
}

function PlayersStep() {
  return (
    <div className="space-y-3">
      <div className="text-xs text-slate-500">
        Aggiungi giocatori in batch. Uno per riga: <code className="bg-slate-200 px-1 rounded">Nome, numero, ruolo</code>.
      </div>
      <div className="rounded-md border border-slate-200 bg-white p-3 font-mono text-[11px] text-slate-700 leading-relaxed">
        <div>Mario Rossi, 10, Attaccante</div>
        <div>Luca Bianchi, 7, Centrocampista</div>
        <div>Giulia Verdi, 1, Portiere</div>
        <div>Stefano Neri, 4, Difensore</div>
        <div>Andrea Esposito, 9, Attaccante<span className="ml-0.5 inline-block w-0.5 h-3 bg-emerald-500 align-middle animate-pulse" /></div>
      </div>
      <div className="text-[10px] text-slate-500">
        Foto, contatti genitori, tessera FIGC: aggiungili dopo dall&apos;Anagrafica.
      </div>
    </div>
  );
}

function TourStep() {
  const items = [
    "Convocazioni e Distinta FIGC ufficiale in PDF",
    "Chat interna con le famiglie in tempo reale",
    "Mister AI: schede allenamento, analisi partita",
    "Modulistica con firma OTP via email",
  ];
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-emerald-700">
        <Rocket size={16} />
        <span className="font-bold text-sm">Sei pronto!</span>
      </div>
      <div className="text-xs text-slate-600">
        Lo spogliatoio digitale è configurato. Ecco cosa puoi fare ora:
      </div>
      <div className="space-y-1.5">
        {items.map((t) => (
          <div key={t} className="flex items-center gap-2 text-[11px] text-slate-700">
            <div className="h-4 w-4 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
              <Check size={10} className="text-emerald-700" />
            </div>
            {t}
          </div>
        ))}
      </div>
    </div>
  );
}
